// src/components/HousingHistoryForm.tsx
import React, { useState, ChangeEvent, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import AddressInput from "../AddressInput";
import PhoneInput from "react-phone-number-input";
import { ApplicationHousingEntryDto } from "../../api/services";

type FormData = Omit<ApplicationHousingEntryDto, "id" | "createdAt">;

interface Props {
  initial?: FormData;
  onSave: (data: FormData) => void;
  onCancel: () => void;
}

const HousingHistoryForm: React.FC<Props> = ({ initial, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormData>(
    initial ?? {
      address: "",
      moveInDate: "",
      moveOutDate: undefined,
      currentResidence: false,
      manager: "",
      phone: "",
      email: "",
    }
  );

  const handleChange = (
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setForm(
      (f) =>
        ({
          ...f,
          [name]:
            type === "checkbox"
              ? checked
              : name === "moveOutDate" && value === ""
                ? undefined
                : value,
        }) as any
    );
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="p-5">
          <h4 className="mb-4 text-base font-semibold text-primary">
            {t("housingHistoryForm.propertyAddress")}
          </h4>
          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="address">
              {t("housingHistoryForm.address")}{" "}
              <span className="text-danger">*</span>
            </label>
            <AddressInput
              id="address"
              name="address"
              value={form.address}
              onChange={(addr) => setForm((f) => ({ ...f, address: addr }))}
              onSelect={(addr) => setForm((f) => ({ ...f, address: addr }))}
              placeholder={t("housingHistoryForm.addressPlaceholder")}
              required
            />
          </div>
          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="moveInDate">
              {t("housingHistoryForm.moveInDate")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              id="moveInDate"
              name="moveInDate"
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
              value={form.moveInDate}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4 flex items-center gap-2">
            <input
              type="checkbox"
              id="currentResidence"
              name="currentResidence"
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/20"
              checked={form.currentResidence}
              onChange={handleChange}
            />
            <label htmlFor="currentResidence" className="text-sm text-gray-700">
              {t("housingHistoryForm.currentResidence")}
            </label>
          </div>
          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="moveOutDate">
              {t("housingHistoryForm.moveOutDate")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              id="moveOutDate"
              name="moveOutDate"
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
              value={form.moveOutDate ?? ""}
              onChange={handleChange}
              disabled={form.currentResidence}
              required={!form.currentResidence}
            />
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="p-5">
          <h4 className="mb-4 text-base font-semibold text-primary">
            {t("housingHistoryForm.propertyOwnerManager")}
          </h4>
          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="manager">
              {t("housingHistoryForm.managerName")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="manager"
              name="manager"
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
              value={form.manager}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="phone">
              {t("housingHistoryForm.phoneNumber")}{" "}
              <span className="text-danger">*</span>
            </label>
            <PhoneInput
              id="phone"
              name="phone"
              international={false}
              defaultCountry="US"
              value={form.phone}
              onChange={(val) => setForm((f) => ({ ...f, phone: val || "" }))}
              placeholder={t("housingHistoryForm.phonePlaceholder")}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
              required
            />
          </div>
          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="email">
              {t("housingHistoryForm.emailAddress")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>
        </div>
      </div>

      <div className="text-center">
        <button
          type="button"
          className="mr-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          onClick={onCancel}
        >
          {t("housingHistoryForm.cancel")}
        </button>
        <button type="submit" className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light">
          {t("housingHistoryForm.save")}
        </button>
      </div>
    </form>
  );
};

export default HousingHistoryForm;
